import { useCallback, useEffect, useRef, useState } from 'react';

import dayjs from 'dayjs';
import padStart from 'lodash/padStart';

const formatCountdown = (secLeft: number): string | null => {
  if (secLeft <= 0) {
    return null;
  }

  const sec = secLeft % 60;
  const min = Math.floor(secLeft / 60);

  return `${padStart(`${min}`, 2, '0')}:${padStart(`${sec}`, 2, '0')}`;
};

export const useCountdownTimer = (
  durationSec: number,
  persistenceKey?: string,
  tickSec: number = 1,
): [string | null, VoidFunction] => {
  const [secLeft, setSecLeft] = useState<number>(0);

  const intervalRef = useRef<NodeJS.Timeout>();

  const startCountdown = useCallback(
    (_durationSec: number, noPersistence?: boolean) => {
      clearInterval(intervalRef.current);

      if (persistenceKey && !noPersistence) {
        const timerEnd = dayjs().add(_durationSec, 'second').valueOf().toString();
        localStorage.setItem(persistenceKey, timerEnd);
      }

      setSecLeft(_durationSec);

      intervalRef.current = setInterval(() => {
        setSecLeft((curr) => {
          const updatedTimeout = Math.max(0, curr - tickSec);

          if (updatedTimeout <= 0) {
            clearInterval(intervalRef.current);

            if (persistenceKey) {
              localStorage.removeItem(persistenceKey);
            }
          }

          return updatedTimeout;
        });
      }, tickSec * 1000);
    },
    [persistenceKey, tickSec],
  );

  useEffect(() => {
    if (!persistenceKey) {
      return;
    }

    const timerEnd = Number(localStorage.getItem(persistenceKey));

    if (isNaN(timerEnd) || !timerEnd) {
      return;
    }

    const leftFromPrevSession = dayjs(timerEnd).diff(dayjs(), 'second');

    if (leftFromPrevSession > 0 && leftFromPrevSession <= durationSec) {
      startCountdown(leftFromPrevSession, true);
    } else {
      localStorage.removeItem(persistenceKey);
    }
  }, [durationSec, persistenceKey, startCountdown]);

  useEffect(
    () => () => {
      clearInterval(intervalRef.current);
    },
    [],
  );

  const launch = useCallback(() => startCountdown(durationSec), [durationSec, startCountdown]);

  return [formatCountdown(secLeft), launch];
};
